import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { LucideIcon } from 'lucide-react';
import { useT } from '../i18n/context.tsx';

/**
 * Toast system — Field Notes rebuild (epic #950 PR-8, formerly the
 * console-shared `createToastSystem` factory instantiation). Same
 * contract: provider-managed queue with TTL auto-dismiss, portal
 * viewport bottom-right, `toast-<variant>` / `toast-dismiss` testids.
 */

export type ToastVariant = 'error' | 'success' | 'info';

export interface Toast {
  readonly id: string;
  readonly variant: ToastVariant;
  readonly message: string;
}

interface ToastContextValue {
  readonly toasts: readonly Toast[];
  readonly push: (variant: ToastVariant, message: string) => string;
  readonly dismiss: (id: string) => void;
  readonly error: (message: string) => string;
  readonly success: (message: string) => string;
  readonly info: (message: string) => string;
}

interface ToastProviderProps {
  readonly children: ReactNode;
  readonly ttlMs?: number;
}

const DEFAULT_TTL_MS = 6000;

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({
  children,
  ttlMs = DEFAULT_TTL_MS,
}: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string): void => {
    const handle = timers.current.get(id);
    if (handle !== undefined) {
      clearTimeout(handle);
      timers.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (variant: ToastVariant, message: string): string => {
      const id = `toast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => [...prev, { id, variant, message }]);
      const handle = setTimeout(() => dismiss(id), ttlMs);
      timers.current.set(id, handle);
      return id;
    },
    [dismiss, ttlMs],
  );

  const error = useCallback((message: string): string => push('error', message), [push]);
  const success = useCallback((message: string): string => push('success', message), [push]);
  const info = useCallback((message: string): string => push('info', message), [push]);

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, push, dismiss, error, success, info }),
    [toasts, push, dismiss, error, success, info],
  );

  useEffect(() => {
    return () => {
      for (const handle of timers.current.values()) {
        clearTimeout(handle);
      }
      timers.current.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return ctx;
}

interface ViewportProps {
  readonly toasts: readonly Toast[];
  readonly onDismiss: (id: string) => void;
}

function ToastViewport({ toasts, onDismiss }: ViewportProps): JSX.Element | null {
  const t = useT();
  if (typeof document === 'undefined') {
    return null;
  }
  if (toasts.length === 0) {
    return null;
  }
  return createPortal(
    <div
      role="region"
      aria-label={t('toast.region_aria')}
      data-testid="toast-viewport"
      className="pointer-events-none fixed bottom-6 right-6 z-50 flex max-w-[calc(100vw-48px)] flex-col items-end gap-2"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>,
    document.body,
  );
}

interface ToastItemProps {
  readonly toast: Toast;
  readonly onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps): JSX.Element {
  const t = useT();
  const skin = variantSkin(toast.variant);
  const IconGlyph = skin.icon;
  return (
    <div
      role={toast.variant === 'error' ? 'alert' : 'status'}
      data-testid={`toast-${toast.variant}`}
      data-toast-id={toast.id}
      className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border px-4 py-3 ${skin.container}`}
    >
      <IconGlyph aria-hidden="true" className={`h-5 w-5 shrink-0 ${skin.iconColor}`} />
      <p className={`m-0 flex-1 break-words text-body leading-snug ${skin.text}`}>
        {toast.message}
      </p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label={t('toast.dismiss_aria')}
        data-testid="toast-dismiss"
        className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/40 ${skin.dismiss}`}
      >
        <X aria-hidden="true" className="h-4 w-4" />
      </button>
    </div>
  );
}

interface Skin {
  readonly container: string;
  readonly icon: LucideIcon;
  readonly iconColor: string;
  readonly text: string;
  readonly dismiss: string;
}

function variantSkin(variant: ToastVariant): Skin {
  switch (variant) {
    case 'error':
      return {
        container: 'border-bad-line bg-bad-tint',
        icon: AlertCircle,
        iconColor: 'text-bad',
        text: 'text-bad',
        dismiss: 'text-bad hover:bg-bad-line/40',
      };
    case 'success':
      return {
        container: 'border-ok-line bg-ok-tint',
        icon: CheckCircle2,
        iconColor: 'text-ok',
        text: 'text-ink',
        dismiss: 'text-ink-low hover:bg-active hover:text-ink',
      };
    default:
      return {
        container: 'border-line bg-card',
        icon: Info,
        iconColor: 'text-forest',
        text: 'text-ink',
        dismiss: 'text-ink-low hover:bg-active hover:text-ink',
      };
  }
}
